import {useEffect, useRef, useState} from "react"
import {Box, CircularProgress, IconButton, Stack} from "@mui/joy"
import {XIcon} from "lucide-react"
import {useTranslation} from "react-i18next"

export type IkeepayCheckoutProps = {
    /** Checkout opened by the API for one transaction; nothing is shown without one */
    checkoutUrl?: string
    onSuccess?: () => void
    onClose?: () => void
    onError?: (message?: string) => void
}

/**
 * Ikeepay's checkout, inline.
 *
 * The card and mobile money forms are Ikeepay's own, so they are loaded in a frame rather than
 * rebuilt here: no card number or phone number ever passes through this app. The frame is hidden
 * until Ikeepay says it is ready, so a blank rectangle is never shown.
 *
 * "ikeepay-success" is a signal to the eye only — the plan is granted by the webhook, against the
 * amount the API priced, never by anything the browser reports.
 */
const IkeepayCheckout = ({checkoutUrl, onSuccess, onClose, onError}: IkeepayCheckoutProps) => {
    const {t} = useTranslation()
    const frameRef = useRef<HTMLIFrameElement>(null)
    const [ready, setReady] = useState(false)

    useEffect(() => {
        setReady(false)

        if (!checkoutUrl)
            return

        let successTimeout: ReturnType<typeof setTimeout>

        const handleMessage = (event: MessageEvent) => {
            // Only the checkout frame is listened to, not any other page that posts to this window
            if (event.source !== frameRef.current?.contentWindow)
                return

            const message = typeof event.data === "string" ? event.data : event.data?.type

            switch (message) {
                case "ikeepay-ready":
                    setReady(true)
                    break
                case "ikeepay-close":
                    onClose?.()
                    break
                case "ikeepay-success":
                    // Ikeepay's own confirmation stays on screen a moment, as their integration does
                    successTimeout = setTimeout(() => onSuccess?.(), 2000)
                    break
                case "ikeepay-error":
                    onError?.(event.data?.message)
                    break
            }
        }

        window.addEventListener("message", handleMessage)

        return () => {
            window.removeEventListener("message", handleMessage)
            clearTimeout(successTimeout)
        }
    }, [checkoutUrl, onSuccess, onClose, onError])

    if (!checkoutUrl)
        return null

    return <Stack sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)"
    }}>
        {/* The payer is looking at a spinner rather than nothing while Ikeepay loads */}
        {!ready && <CircularProgress/>}

        <Box sx={{
            position: "relative",
            display: ready ? "block" : "none",
            width: "100%",
            maxWidth: 450,
            height: {xs: "100%", md: "85vh"},
            padding: {md: 2}
        }}>
            <IconButton variant={"soft"} color={"neutral"} onClick={onClose} aria-label={t("close")}
                        sx={{position: "absolute", top: {xs: 8, md: -40}, right: 16, zIndex: 1}}>
                <XIcon/>
            </IconButton>

            <Box component={"iframe"}
                 ref={frameRef}
                 title={"Ikeepay"}
                 src={checkoutUrl}
                 allow={"payment"}
                 sx={{
                     width: "100%",
                     height: "100%",
                     border: "none",
                     background: "transparent",
                     borderRadius: {md: "2rem"}
                 }}/>
        </Box>
    </Stack>
}

export default IkeepayCheckout
